import { useState } from "react";
import { ColumnAlign } from "../widgets/ColumnAlign";
import { PersistOrtho } from "./persistOrtho";
import { ChoiceList, ChoiceDataGroup } from "../widgets/ChoiceList";
import { KeyMap } from "./keyToOrthoMapping";
import { RuleSet } from "../../rules/ruleSet";
import { isOrthoRulesError, orthoRulesErrorToString } from "./serializeOrthoRules";
import { browseAndLoad } from "../../persist/browseAndLoad";
import { isVersionError, versionErrorToString, versionStringToFonimKeyMap, versionStringToFonimRules } from "./versionFiles";
import { sampleKeysMaps } from "./sampleKeyMaps";
import { sampleRules } from "./sampleRules";

/** load & track custom keyboards & rules */
export function LoadBar(props: {
  version: number,
  width: number,
  onLoadKeys: (name: string, newKeys: KeyMap) => void,
  onLoadRules: (name: string, newRules: RuleSet) => true | string[];
}) {
  const [lastVersion, setLastVersion] = useState(0);
  const [persist] = useState(new PersistOrtho());
  const [commonList, setCommonList] = useState([''].concat(persist.getSavedCommon()));
  const [selectedCommon, setSelectedCommon] = useState('');
  const [keysList, setKeysList] = useState([''].concat(persist.getSavedKeys()));
  const [selectedKeys, setSelectedKeys] = useState('');
  const [rulesList, setRulesList] = useState([''].concat(persist.getSavedRules()));
  const [selectedRules, setSelectedRules] = useState('');
  const [error, setError] = useState('');

  if (props.version !== lastVersion) {
    setLastVersion(props.version);
    setCommonList([''].concat(persist.getSavedCommon()));
    setKeysList([''].concat(persist.getSavedKeys()));
    setRulesList([''].concat(persist.getSavedRules()));
  }

  function loadSelectedCommon(name: string, data?: string) {
    loadSelectedKeys(name, data);
    loadSelectedRules(name, data);
    setSelectedCommon(name,);
  }

  function loadSelectedKeys(name: string, data?: string) {
    setSelectedCommon('');
    setSelectedKeys(name);
    setError('');
    if (data === idLoadFile) {
      browseAndLoad(loadKeysFile);
    } else if (data === idSample) {
      props.onLoadKeys(name, sampleKeysMaps[name]);
    } else {
      const map = persist.loadKeys(name);
      if (!isVersionError(map) && map !== null) {
        props.onLoadKeys(name, map);
      }
    }
  }
  function loadKeysFile(filename: string, contents: string) {
    const map = versionStringToFonimKeyMap(contents);
    if (isVersionError(map)) {
      setError(versionErrorToString(map));
    } else {
      const name = filename.endsWith('.txt') ? filename.substring(0, filename.length - 4) : filename;
      props.onLoadKeys(name, map);
      setError('');
    }
  }

  function loadSelectedRules(name: string, data?: string) {
    setSelectedCommon('');
    setSelectedRules(name);
    setError('');
    if (data === idLoadFile) {
      browseAndLoad(loadRulesFile);
    } else if (data === idSample) {
      props.onLoadRules(name, sampleRules[name]);
    } else {
      const rules = persist.loadRules(name);
      if (!isOrthoRulesError(rules) && !isVersionError(rules) && rules !== null) {
        props.onLoadRules(name, rules);
      }
    }
  }
  function loadRulesFile(filename: string, contents: string) {
    const output = versionStringToFonimRules(contents);
    if (isOrthoRulesError(output)) {
      const error = orthoRulesErrorToString(output);
      setError(error);
    } else if (isVersionError(output)) {
      const error = versionErrorToString(output);
      setError(error);
    } else {
      const name = filename.endsWith('.txt') ? filename.substring(0, filename.length - 4) : filename;
      const result = props.onLoadRules(name, output);
      setError(result === true ? '' : result.join('\n'));
    }
  }

  return (<>
    <ColumnAlign width={props.width}>{[
      <span key={1}>
        load:&nbsp;
        <ChoiceList items={commonList} itemGroups={[samples]} selectedItem={selectedCommon} onSelect={loadSelectedCommon} />
      </span>,
      <span key={2}>
        keys:&nbsp;
        <ChoiceList items={keysList} itemGroups={[samples, groupLoadData]} selectedItem={selectedKeys} onSelect={loadSelectedKeys} />
      </span>,
      <span key={3}>
        rules:&nbsp;
        <ChoiceList items={rulesList} itemGroups={[samples, groupLoadData]} selectedItem={selectedRules} onSelect={loadSelectedRules} />
      </span>,
    ]}</ColumnAlign>
    {error === '' ? undefined : <><br />error: {error}</>}
  </>);
}

const idLoadFile = 'load-file';
const groupLoadData: ChoiceDataGroup = {
  items: ['load file...'], data: idLoadFile
};

// used by both keymaps & rules, since they're the same set of names
const idSample = 'sample';
const samples: ChoiceDataGroup = {
  name: 'samples', items: Object.getOwnPropertyNames(sampleKeysMaps), data: idSample,
}
